import { useQuery } from "@tanstack/react-query";
import type { ApiClient } from "../../../app/api/client";
import { authQueryScope } from "../../../app/query/authQueryScope";
import { personDetailQueryKeys } from "./personDetailQueryKeys";

/** Spiegel von `CheckInEligibility` / `CheckInEligibilityReason` (Backend, Wirewerte). */
export type PersonOverviewAccessStatus = "entitled" | "limited" | "not_entitled" | "blocked";
export type PersonOverviewAccessReason = "none" | "manual" | "reminder_level" | "rest_period" | "studio_limited" | "no_basis";

/** Spiegel von `PersonOverviewTodoKind` (Backend, Wirewerte). */
export type PersonOverviewTodoCode =
  | "open_items_overdue"
  | "access_blocked"
  | "sepa_mandate_missing"
  | "app_request_pending"
  | "contract_ending"
  | "rest_period_ending"
  | "care_follow_up_due"
  | "no_valid_contract";

export type PersonOverviewTodo = {
  code: PersonOverviewTodoCode;
  urgent: boolean;
  date?: string | null;
  amount?: number | null;
  count?: number | null;
  targetId?: string | null;
  name?: string | null;
  /** Unterart je Code, z. B. `manual`/`reminder_level`, `iban`/`mandate`, `cancelled`/`term_end`. */
  detail?: string | null;
};

export type PersonOverviewPackage = {
  id: string;
  name: string;
  startDate: string;
  effectiveEndDate?: string | null;
  cancellationEffectiveDate?: string | null;
  renews: boolean;
  isDirectDebit: boolean;
  restPeriod?: { id: string; startDate: string; endDate: string } | null;
};

/** Antwort von `GET /api/v1/persons/{id}/overview`. Ein Block ist `null`, wenn das Recht fehlt. */
export type PersonOverviewDto = {
  access: {
    status: PersonOverviewAccessStatus;
    reason: PersonOverviewAccessReason;
    manualBlocked: boolean;
    blockingReminderLevelName?: string | null;
    lastCheckInAt?: string | null;
  } | null;
  homeStudio: { id?: string | null; name?: string | null };
  contracts: { running: number; total: number; packages: PersonOverviewPackage[] } | null;
  openItems: { openAmount: number; count: number; overdueCount: number; oldestOverdueDueDate?: string | null } | null;
  finance: {
    nextContributionDate?: string | null;
    nextContributionAmount?: number | null;
    walletCount: number;
    currencyBalance: number;
    pointsBalance: number;
    hasUsablePointsWallet: boolean;
  } | null;
  todos: PersonOverviewTodo[];
};

/**
 * Die eine Abfrage der Personenübersicht. Der Schlüssel beginnt mit `person.overview(personId)`, damit
 * `invalidatePersonContractData`/`invalidatePersonFinanceData` und Änderungen in der Übersicht selbst
 * Lagebild und „Zu tun“ mitnehmen.
 */
export function usePersonOverview({
  api,
  authInfo,
  personId,
  studioIdsParam,
  enabled
}: {
  api: ApiClient;
  authInfo: Parameters<typeof authQueryScope>[1];
  personId: string;
  studioIdsParam: string;
  enabled: boolean;
}) {
  const authScope = authQueryScope(api, authInfo).join(":");
  return useQuery({
    queryKey: [...personDetailQueryKeys.person.overview(personId), "bundle", { studioScope: studioIdsParam, authScope }],
    enabled: enabled && Boolean(personId),
    retry: false,
    queryFn: () => {
      const query = new URLSearchParams();
      if (studioIdsParam) query.set("studioIds", studioIdsParam);
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      return api.get<PersonOverviewDto>(`/api/v1/persons/${encodeURIComponent(personId)}/overview${suffix}`, { suppressGlobalErrorToast: true });
    }
  });
}
